import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { mediaFileUrl } from '../../shared/mediaFileUrl';
import { LockedPill } from '../../shared/Pill';
import SaveButton from '../../shared/SaveButton';
import FieldHelp from '../../shared/FieldHelp';
import ParagraphText from '../../shared/ParagraphText';
import { CURRENCY_LABELS } from '../../shared/numberFormat';
import { useAnchorPriceGroupName } from '../../shared/useAnchorPriceGroupName';
import { AddressCheckPill, PostcodeCheckPill } from '../../shared/AddressFields';
import { companyInfoAddress } from '../../shared/addressFormat';
import OwnedBrandsCard from '../crm/Leads/OwnedBrandsCard';

// Generic "no logo" placeholder — same self-contained-SVG approach as the
// Product image placeholder (PricelistExtendedReport.js), resized to a
// logo's landscape footprint instead of a square product photo. Shown here
// and in CompanySettings.js's edit-mode LogoField so "no logo yet" always
// renders as an image-shaped slot rather than text. `width`/`height` let
// each caller pick its own footprint (the edit form's LogoField wants a
// bigger upload target than this page's compact display header).
export function PlaceholderLogo({ width = 160, height = 60 }) {
  return (
    <svg width={width} height={height} viewBox="0 0 160 60" style={{ display: 'block' }}>
      <rect x="1" y="1" width="158" height="58" rx="6" fill="#f6f8fa" stroke="#cdd5df" strokeWidth="1.5" />
      <circle cx="57" cy="22" r="6" fill="#cdd5df" />
      <path d="M37 46 L61 26 L79 40 L95 28 L123 46 Z" fill="#cdd5df" />
    </svg>
  );
}

// Format landline as '02 XXXX-YYYY'
function formatDisplayPhone(phone, type) {
  if (type === 'landline' && phone) {
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10 && digits.startsWith('0')) {
      return digits.slice(0,2) + ' ' + digits.slice(2,6) + '-' + digits.slice(6,10);
    }
  }
  return phone;
}

// International dial form — country code + number with the AU trunk
// prefix (the leading 0 in "02.../04...") dropped, since glued together
// as-is it produces an undialable "+610212345678" (same bug class fixed
// on the Pricelist's contact card — see useCompanyName.js). Bureau numbers
// (1300/1800) have no trunk 0 to drop.
function formatIntlPhone(countryCode, phone, type) {
  if (!phone) return '';
  const domestic = formatDisplayPhone(phone, type);
  const hasTrunkZero = (type === 'landline' || type === 'mobile') && phone.startsWith('0');
  const local = hasTrunkZero ? domestic.slice(1).trim() : domestic;
  return [countryCode, local].filter(Boolean).join(' ');
}

// Same handle-from-URL extraction the Pricelist's contact card uses
// (PricelistContactBlock.js) — kept as a local copy rather than a shared
// import since that file is a print-report concern and this is a
// settings-form concern (same precedent as this file's own
// formatDisplayPhone/formatDisplayAddress vs. QuoteReportView.js's).
function instagramHandle(url) {
  const match = url.match(/instagram\.com\/([^/?#]+)/i);
  return match ? `@${match[1]}` : url;
}

// `localityNote` sits beside the Suburb line: Australia Post's check of the
// Suburb, State and Postcode (#158).
function formatDisplayAddress(data, localityNote) {
  // Compose address as: [Unit/Suite], [Street]\n[District, Suburb, State Postcode]\n[Country]
  // Country is always shown last, including for Australia (#165) — every
  // printed/displayed address block shows it regardless of country.
  const unit = data.address_unit ? `${data.address_unit}, ` : '';
  const street = data.address_street ? `${data.address_street}` : '';
  const district = data.address_district || '';
  const city = data.address_city ? data.address_city : '';
  const state = data.address_state ? data.address_state : '';
  const postcode = data.address_postcode ? data.address_postcode : '';
  const line1 = `${unit}${street}`.replace(/, $/, '').trim();
  const line2 = [district, city, state, postcode].filter(Boolean).join(', ').trim();
  const line3 = data.country || '';
  if (!line1 && !line2 && !line3) return <span style={{color:'#bbb'}}>N/A</span>;
  return <span><span>{line1}</span>{line2 && <><br/>{line2}{localityNote && <> {localityNote}</>}</>}{line3 && <><br/>{line3}</>}</span>;
}

// Same grouped-section treatment as CompanySettings.js's edit form (itself
// matching ProductForm.js's sectionStyle/sectionHeaderStyle) — kept as a
// local duplicate rather than a shared import since CompanySettings.js
// already imports this file, and a shared constants module for two small
// style objects isn't worth the extra file.
const sectionStyle = { padding: 16, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };
const sectionHeaderStyle = { margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 };

// The Brands the business itself owns (#430), with Add Brand — the same card
// an Organisation's page shows for the Brands it owns.
function HouseBrandsCard() {
  const [business, setBusiness] = useState(null);
  useEffect(() => {
    axios.get('/api/brands/this-business')
      .then((res) => setBusiness(res.data.data || null))
      .catch(() => setBusiness(null));
  }, []);
  if (!business) return null;
  return <OwnedBrandsCard entity={business} origin={{ label: 'Company Information', path: '/company-info' }} />;
}

function CompanySettingsDisplay({ data, onEdit, onRecheck, rechecking }) {
  const anchorName = useAnchorPriceGroupName();
  const formattedPhone = formatDisplayPhone(data.phone_number, data.phone_type);
  const formattedIntlPhone = formatIntlPhone(data.phone_country_code, data.phone_number, data.phone_type);
  const formattedAddress = formatDisplayAddress(data, <PostcodeCheckPill status={data.address_postcode_check} onRecheck={onRecheck} checking={rechecking} />);
  const labelStyle = { color: '#888', fontWeight: 500, minWidth: 130, textAlign: 'left', display: 'inline-block', verticalAlign: 'top' };
  const valueStyle = { color: '#222', fontWeight: 500, textAlign: 'left', display: 'inline-block' };
  const rowStyle = { marginBottom: 12, fontSize: 14, textAlign: 'left' };

  return (
    <div style={{ width: '100%', maxWidth: 1020, boxSizing: 'border-box', margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
        {data.logo ? (
          <img src={mediaFileUrl(data.logo.id)} alt="Company logo" style={{ maxWidth: 110, maxHeight: 40, objectFit: 'contain' }} />
        ) : (
          <PlaceholderLogo width={110} height={40} />
        )}
        {data.site_icon && (
          <img src={mediaFileUrl(data.site_icon.id)} alt="Site Icon" title="Site Icon, shown in browser tabs" style={{ width: 32, height: 32, objectFit: 'contain' }} />
        )}
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Company Information</h2>
      </div>

      <div style={sectionStyle}>
        <h4 style={sectionHeaderStyle}>Company Details</h4>
        <div style={rowStyle}><span style={labelStyle}>Company Name:</span> <span style={valueStyle}>{data.company_name || <span style={{color:'#bbb'}}>N/A</span>}</span></div>
        <div style={rowStyle}><span style={labelStyle}>ABN:</span> <span style={valueStyle}>{data.abn || <span style={{color:'#bbb'}}>N/A</span>}</span></div>
        <div style={{ ...rowStyle, marginBottom: 0, display: 'flex' }}>
          <span style={labelStyle}>About Us:</span>
          {data.about_us
            ? <ParagraphText text={data.about_us} style={{ ...valueStyle, flex: 1 }} />
            : <span style={{ color: '#bbb' }}>N/A</span>}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
        <div style={{ ...sectionStyle, flex: 1 }}>
          <h4 style={sectionHeaderStyle}>Address</h4>
          <div style={{ ...rowStyle, marginBottom: 0 }}><span style={labelStyle}>Location:</span> <span style={valueStyle}>{formattedAddress}</span></div>
          <div style={{ marginTop: 8 }}><AddressCheckPill address={companyInfoAddress(data)} /></div>
        </div>
        <div style={{ ...sectionStyle, flex: 1 }}>
          <h4 style={sectionHeaderStyle}>Contact</h4>
          <div style={rowStyle}>
            <span style={labelStyle}>Phone:</span>
            <span style={valueStyle}>
              {data.phone_number ? (
                <>
                  {formattedPhone} <span style={{ color: '#1976d2', fontWeight: 500 }}>({data.phone_type})</span>
                  {formattedIntlPhone && <div style={{ color: '#888', fontSize: 12, fontWeight: 400 }}>{formattedIntlPhone}</div>}
                </>
              ) : <span style={{color:'#bbb'}}>N/A</span>}
            </span>
          </div>
          <div style={rowStyle}><span style={labelStyle}>Email:</span> <span style={valueStyle}>{data.email || <span style={{color:'#bbb'}}>N/A</span>}</span></div>
          <div style={rowStyle}><span style={labelStyle}>Website:</span> <span style={valueStyle}>{data.website || <span style={{color:'#bbb'}}>N/A</span>}</span></div>
          <div style={{ ...rowStyle, marginBottom: 0 }}><span style={labelStyle}>Instagram:</span> <span style={valueStyle}>{data.instagram_url ? instagramHandle(data.instagram_url) : <span style={{color:'#bbb'}}>N/A</span>}</span></div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
        <div style={{ ...sectionStyle, flex: 1 }}>
          <h4 style={sectionHeaderStyle}>Localisation</h4>
          <div style={rowStyle}><span style={labelStyle}>Default Currency:</span> <span style={valueStyle}>{data.currency_code || <span style={{color:'#bbb'}}>N/A</span>}</span></div>
          <div style={{ ...rowStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={labelStyle}>Currency Label:</span> <span style={valueStyle}>{(CURRENCY_LABELS.find((l) => l.value === data.currency_label) || CURRENCY_LABELS[0]).label.split(' (')[0]}</span>
            <FieldHelp fieldKey="foundation-company-settings.currency-label" pageKey="company-info" label="Currency Label">
              How an amount is labelled. Only the Default Currency can show as a bare symbol ($); an amount in any other currency is always marked (US$ or USD), so it can't be mistaken for the Default Currency.
            </FieldHelp>
          </div>
          <div style={{ ...rowStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={labelStyle}>Timezone:</span> <span style={valueStyle}>{data.timezone ? data.timezone.replace(/_/g, ' ') : <span style={{color:'#bbb'}}>N/A</span>}</span>
            <FieldHelp fieldKey="foundation-company-settings.timezone" pageKey="company-info" label="Timezone">
              The zone every business date is recorded in: today's date, sent-on dates, due dates and price effective dates. Dates and times on screen and in reports also show in this zone. Changing it applies to dates recorded from then on.
            </FieldHelp>
          </div>
          <div style={{ ...rowStyle, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={labelStyle}>Date Format:</span> <span style={valueStyle}>{data.date_format || 'dd/mm/yyyy'}</span>
            <FieldHelp fieldKey="foundation-company-settings.date-format" pageKey="company-info" label="Date Format">
              How every date is shown and typed, for everyone: date fields, lists, detail pages, filters, reports, pricelist PDFs and emails.
            </FieldHelp>
          </div>
          <div style={{ ...rowStyle, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={labelStyle}>Number Format:</span> <span style={valueStyle}>{data.number_format || '1,234.56'}</span>
            <FieldHelp fieldKey="foundation-company-settings.number-format" pageKey="company-info" label="Number Format">
              The thousands and decimal separators for every number, shown or typed, for everyone: prices, costs, quantities, percentages, exchange rates and weights, on screen, in reports, pricelist PDFs and emails.
            </FieldHelp>
          </div>
        </div>
        {data.id && (
          <div style={{ ...sectionStyle, flex: 1 }}>
            <h4 style={sectionHeaderStyle}>Pricing</h4>
            <div style={{ ...rowStyle, marginBottom: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={labelStyle}>Track {anchorName}:</span> <span style={valueStyle}>{data.track_rrp ? 'Yes' : 'No'}</span>
              <FieldHelp fieldKey="foundation-company-settings.track-rrp" pageKey="company-info" label={`Track ${anchorName}`}>
                A one-time, permanent choice about whether your business tracks {anchorName}. Confirmed once when Company Settings is first created — it can't be changed afterward.
              </FieldHelp>
              <LockedPill title="This choice was confirmed and locked when Company Settings was first created — it can't be changed." />
            </div>
          </div>
        )}
      </div>

      {data.id && (
        <div style={{ marginTop: 16 }}>
          <HouseBrandsCard />
        </div>
      )}

      <div style={{ marginTop: 20 }}>
        <SaveButton onClick={onEdit} type="button">Edit</SaveButton>
      </div>
    </div>
  );
}

export default CompanySettingsDisplay;
